import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {
  CartEvent,
  LegacyTicket,
  PriceClass,
  PriceType,
  Sector,
  TicketsRequest, TicketsResponse,
  UnassignedTicket
} from '../../../../generated/sales-terminal-client';
import {CartService, SelectedPriceZone, SelectedTicketsMap} from '../../../../services/cart/cart.service';
import {RouteService} from '../../../../services/route/route.service';
import {ConfirmDialogComponent} from '../../../../components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {cloneDeep} from 'lodash-es';
import {TicketTypeService} from '../../../../services/ticket-type/ticket-type.service';
import {MatDialogRef} from '@angular/material/dialog';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-selected-tickets',
  templateUrl: './selected-tickets.component.html',
  styleUrls: ['./selected-tickets.component.scss']
})
export class SelectedTicketsComponent implements OnDestroy {
  @Input() public eventId: number;
  private _selectedTicketsRef: SelectedTicketsMap;
  private _eventSectors: Sector[] = [];
  @Input()
  public set eventSectors(sectors: Sector[]) {
    this._eventSectors = sectors;
    this.mapSelectedTickets();
  };
  public get eventSectors(): Sector[] {
    return this._eventSectors || [];
  }
  private _selectedTicketTypes: UnassignedTicket[] = [];
  @Input()
  public set selectedTicketTypes(ticketTypes: UnassignedTicket[]) {
    this._selectedTicketTypes = ticketTypes;
    this.mapSelectedTicketTypes();
  }
  public get selectedTicketTypes(): UnassignedTicket[] {
    return this._selectedTicketTypes || [];
  }
  @Output() public removeSeats: EventEmitter<LegacyTicket[]> = new EventEmitter<LegacyTicket[]>();
  @Output() public isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  public expandedSectors: number[] = [];
  public ticketTypesCollapsed: boolean;
  private _sectorsWithSelectedTickets: Sector[] = [];
  private _sectorPriceZonesWithSelectedTickets: Record<number, PriceClass[]> = {};

  constructor(public cartService: CartService,
              public ticketTypeService: TicketTypeService,
              private dialog: MatDialog,
              private routeService: RouteService,
              private translateService: TranslateService) {}

  public ngOnDestroy(): void {
    this.ticketTypeService.clearSubscriptions();
  }

  public getSectorTickets(sectorId: number): LegacyTicket[] {
    return this.cartService.selectedTicketsMap.sectors.find(sector => sector.sector.id === sectorId).tickets || [];
  }
  public getSectorPriceZoneTickets(sectorId: number, priceZoneId: number): LegacyTicket[] {
    return this.cartService.selectedTicketsMap.sectors.find(sector => sector.sector.id === sectorId)
      .priceZones.find(priceZone => priceZone.priceZone.id === priceZoneId).tickets || [];
  }

  public get getSectorsWithSelectedTickets(): Sector[] {
    if (JSON.stringify(this._selectedTicketsRef?.selectedTickets) !== JSON.stringify(this.cartService.selectedTicketsMap?.selectedTickets)) {
      this.mapSelectedTickets();
    }
    return this._sectorsWithSelectedTickets;
  }
  public getPriceZonesWithSelectedTickets(sectorId: number): PriceClass[] {
    return this._sectorPriceZonesWithSelectedTickets[sectorId];
  }

  public toggleSelectedTicketSector(sectorId: number): void {
    if (this.expandedSectors.includes(sectorId)) {
      this.expandedSectors.splice(this.expandedSectors.indexOf(sectorId), 1);
    } else {
      this.expandedSectors.push(sectorId);
    }
  }

  public confirmSectorClear(sectorId: number): void {
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_cart_sector_clear_confirmation_title'),
        description: this.translateService.instant('general.api_cart_sector_clear_confirmation_description'),
        buttonCancel: this.translateService.instant('general.api_general_cancel'),
        buttonOk: this.translateService.instant('general.api_cart_sector_clear')
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.removeSeats.emit(this.getSectorTickets(sectorId));
      }
    });
  }

  private mapSelectedTickets(): void {
    this._sectorsWithSelectedTickets = [];
    this._sectorPriceZonesWithSelectedTickets = {};
    this.cartService.selectedTicketsMap.sectors = [];
    if (this.cartService.selectedTicketsMap.selectedTickets?.length) {
      this._eventSectors.forEach((sector) => {
        const selectedPriceZones: SelectedPriceZone[] = [];
        const priceZones: PriceClass[] = [];
        if (sector.priceClasses?.length && Array.isArray(sector.priceClasses)) {
          sector.priceClasses.forEach(priceZone => {
            const priceZoneTickets: LegacyTicket[] = this.cartService.selectedTicketsMap.selectedTickets.filter(ticket => ticket.sectorId === sector.id && ticket.priceClassId === priceZone.id);
            selectedPriceZones.push({
              priceZone: priceZone,
              tickets: priceZoneTickets
            });
            if (priceZoneTickets?.length) {
              priceZones.push(priceZone);
            }
          });
        }
        const sectorTickets = this.cartService.selectedTicketsMap.selectedTickets.filter(ticket => ticket.sectorId === sector.id);
        if (sectorTickets.length) {
          this.cartService.selectedTicketsMap.sectors.push({
            sector: sector,
            tickets: sectorTickets,
            priceZones: selectedPriceZones
          });
          if (!!sectorTickets?.length) {
            this._sectorsWithSelectedTickets.push(sector);
            this._sectorPriceZonesWithSelectedTickets[sector.id] = priceZones;
          }
        }
      });
    }
    this._selectedTicketsRef = cloneDeep(this.cartService.selectedTicketsMap);
  }

  private mapSelectedTicketTypes(): void {
    if (this.cartService.selectedTicketsMap?.event) {
      this.ticketTypeService.initTicketTypeHandling(this.cartService, [this.cartService.selectedTicketsMap.event as CartEvent]);
    }
  }

  public getAvailableTicketPrices(ticket: LegacyTicket): PriceType[] {
    return this.cartService.selectedTicketsMap?.event?.availablePriceMap[ticket.sectorId + '_' + ticket.priceClassId] || [];
  }

  public changeTicketTypeCount(ticketTypeUuid: string, count?: number): void {
    this.ticketTypeService.changeTicketTypeCount(ticketTypeUuid, this.eventId, count);
  }
  public getTicketTypeMax(id: string): number {
    return this.cartService.calculateMaxTickets(this.ticketTypeService.getTicketTypeInitialCount(id) + this.ticketTypeService.getTicketTypeAvailable(id, this.eventId));
  }

  public confirmTicketTypesClear(): void {
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_cart_ticket_types_clear_confirmation_title'),
        description: this.translateService.instant('general.api_cart_ticket_types_clear_confirmation_description'),
        buttonCancel: this.translateService.instant('general.api_general_cancel'),
        buttonOk: this.translateService.instant('general.api_cart_ticket_types_clear')
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const requests: TicketsRequest[] = [];
        this.cartService.selectedTicketsMap.selectedTicketTypes.forEach(ticketType =>
          requests.push({ticketTypeUuid: ticketType.ticketTypeUuid, quantity: ticketType.quantity*-1}));
        this.ticketTypeService.typesLoading = true;
        this.cartService.setUnassignedTickets(this.eventId, requests).pipe(
          finalize(() => this.ticketTypeService.typesLoading = false)
        ).subscribe(response => this.updateCart(response));
      }
    });
  }
  public removeTicketType(ticketType: UnassignedTicket): void {
    this.ticketTypeService.typesLoading = true;
    this.cartService.setUnassignedTickets(this.eventId, [{ticketTypeUuid: ticketType.ticketTypeUuid, quantity: ticketType.quantity*-1}]).pipe(
      finalize(() => this.ticketTypeService.typesLoading = false)
    ).subscribe(response => this.updateCart(response));
  }

  private updateCart(response: TicketsResponse): void {
    this.cartService.cart = response.cart;
    this.cartService.eventPricing = response.pricing;
    this.cartService.updateCartForEvent(this.eventId);
  }
}
